<template>
  <div class="container mt-4">
    <TopNav>
      <NavItem
        class="float-left pl-0"
        icon="far fa-file-upload"
        :text="$t('SETTINGS.BUTTON.ADJUST_PLAN')"
        @click.native="$router.push({ name: 'settings-adjust-plan' })"
      />
    </TopNav>
    <div class="row mb-3">
      <div class="col h1 mt-4 mb-4">
        {{ $t('SETTINGS.TITLE.ACCOUNT_INFORMATION') }}
      </div>
    </div>
    <div class="row" v-if="accountInfo">
      <div class="col-md-3 mb-2" v-if="teacher">
        <HMInfoTile
          :top-text="$t('SETTINGS.LABEL.YOUR_PLAN')"
          :data="teacher.subscription_plan.display_name"
          :unit="teacher.subscription_interval"
        />
      </div>
      <div class="col-md-3 mb-2">
        <HMInfoTile
          :top-text="
            isActiveSubscription
              ? $t('SETTINGS.LABEL.RENEWS_ON')
              : $t('SETTINGS.LABEL.VALID_UNTIL')
          "
          :data="formatDate(accountInfo.plan_information.expires, 'MMM D', '-')"
          :unit="formatDate(accountInfo.plan_information.expires, 'YYYY', ' ')"
        />
      </div>
      <div class="col-md-3 mb-2">
        <HMInfoTile
          :top-text="$t('SETTINGS.LABEL.STORAGE')"
          :data="calculatedStorageInfo || '-'"
          :unit="calculatedStorageInfo && $t('SETTINGS.LABEL.GB')"
        />
      </div>
      <div class="col-md-3 mb-2">
        <HMInfoTile
          :top-text="$t('SETTINGS.LABEL.JOINED')"
          :data="formatDate(accountInfo.plan_information.joined, 'MMM D', '-')"
          :unit="formatDate(accountInfo.plan_information.joined, 'YYYY', ' ')"
        />
      </div>
    </div>
    <hr class="my-4" />
    <div class="row mb-3">
      <div class="col h2">
        {{ $t('SETTINGS.TITLE.BILLING_CARD') }}
      </div>
      <div class="col align-self-center">
        <div class="float-right">
          <NavItem
            icon="far fa-clock"
            :text="$t('SETTINGS.TEXT.SEE_BILLING_INFO')"
            @click.native="$router.push({ name: 'settings-billing' })"
            :iconOnRight="true"
          />
        </div>
      </div>
    </div>
    <div class="border rounded p-2 d-flex" v-if="accountInfo">
      <div class="col d-flex pl-1">
        <p class="mb-0">{{ $t('SETTINGS.TEXT.SUBSCRIBED_WITH') }}</p>
        <img
          :src="getCardLogo(accountInfo.card_info.brand)"
          height="25px"
          class="ml-2 mr-2"
        />
        <p class="mb-0">{{ $t('SETTINGS.TEXT.ENDING_IN') }}</p>
        {{ accountInfo.card_info.last4 }}
      </div>
      <div class="col align-self-center pr-0">
        <div class="float-right">
          <HMTableButton icon="pen" @click="editCardInfo" />
        </div>
      </div>
    </div>
    <hr class="my-4" />
    <div class="row">
      <div class="col mb-2">
        <h2>{{ $t('SETTINGS.TITLE.CONTACT_INFORMATION') }}</h2>
      </div>
      <div class="col align-self-center">
        <div class="float-right">
          <NavItem
            icon="far fa-pen"
            :text="$t('SETTINGS.BUTTON.EDIT_CONTACT_INFO')"
            @click.native="openTeacherInfoForm"
            :iconOnRight="true"
          />
        </div>
      </div>
    </div>
    <div class="row mt-2">
      <div class="col-8" v-if="teacher">
        {{ teacherInfoShorthand }}
      </div>
    </div>
    <hr class="my-4" />
    <div class="row">
      <div class="col h2">
        {{ $t('SETTINGS.TITLE.TIMEZONE_SETTINGS') }}
      </div>
    </div>
    <div class="row mt-3 mb-4">
      <div class="col" v-if="user">
        <v-select
          :options="timezones"
          label="name"
          code="value"
          v-model="user.timezone"
          :reduce="r => r.value"
          @input="setTimezone"
        >
        </v-select>
      </div>
    </div>
    <hr class="mt-1 mb-4" />
    <div class="row mb-4">
      <div class="col h2">
        {{ $t('SETTINGS.TITLE.CHANGE_PASSWORD') }}
      </div>
      <div class="col align-self-center">
        <div class="float-right d-flex">
          <NavItem
            icon="far fa-pen"
            :text="$t('SETTINGS.TITLE.CHANGE_PASSWORD')"
            :iconOnRight="true"
            @click.native="openPasswordForm"
          />
        </div>
      </div>
    </div>
    <div class="password-form col rounded mt-1 mb-4" v-if="passwordFormOpen">
      <form @submit.prevent="updatePassword">
        <div class="row pt-3">
          <div class="col-6">
            <HMTextInput
              :label="$t('SETTINGS.LABEL.NEW_PASSWORD_1')"
              v-model="newPassword1"
              type="password"
            />
          </div>
          <div class="col-6">
            <HMTextInput
              :label="$t('SETTINGS.LABEL.NEW_PASSWORD_2')"
              v-model="newPassword2"
              type="password"
            />
          </div>
        </div>
        <div class="invalid-feedback d-block mb-2" v-show="formError">
          {{ formError }}
        </div>
        <div class="row">
          <div class="col mb-3">
            <HMButton
              color="outline-gray"
              type="button"
              @click="closePasswordForm"
            >
              {{ $t('GENERAL.BUTTON.DISCARD') }}
            </HMButton>
            <div class="float-right">
              <HMButton class="btn-primary" :loading="inProgress" type="submit">
                {{ $t('GENERAL.BUTTON.SAVE_CHANGES') }}
              </HMButton>
            </div>
          </div>
        </div>
      </form>
    </div>
    <hr class="mt-1 mb-4" />
    <div class="row mb-4">
      <div class="col-12 h2 mb-4">
        {{ $t('SETTINGS.TITLE.EMAIL_PREFERENCES') }}
      </div>
      <div class="col-6 align-self-center mb-2">
        <h5 class="mb-0">
          {{ $t('SETTINGS.LABEL.SEND_EMAIL_ON_NEW_PURCHASE') }}
        </h5>
      </div>
      <div class="col-6 align-self-center mb-2">
        <div class="float-right">
          <HMSwitch
            v-model="teacher.send_new_sale_email"
            @change="setNewPurchaseEmailsEnabled"
          />
        </div>
      </div>
      <div class="col-6 align-self-center mb-2">
        <h5 class="mb-0">
          {{ $t('SETTINGS.LABEL.SEND_EMAIL_ON_NEW_TIP') }}
        </h5>
      </div>
      <div class="col-6 align-self-center mb-2">
        <div class="float-right">
          <HMSwitch
            v-model="teacher.send_new_tip_email"
            @change="setNewTipEmailsEnabled"
          />
        </div>
      </div>
      <div class="col-6 align-self-center mb-2">
        <h5 class="mb-0">
          {{ $t('SETTINGS.LABEL.SEND_EMAIL_ON_SUBSCRIPTION_CANCELLATION') }}
        </h5>
      </div>
      <div class="col-6 align-self-center mb-2">
        <div class="float-right">
          <HMSwitch
            v-model="teacher.send_subscription_cancellation_email"
            @change="setSubscriptionCancellationEmailsEnabled"
          />
        </div>
      </div>
      <div class="col-6 align-self-center mb-2">
        <h5 class="mb-0">
          {{ $t('SETTINGS.LABEL.SEND_EMAIL_ON_PAYMENT_PLAN_CANCELLATION') }}
        </h5>
      </div>
      <div class="col-6 align-self-center mb-2">
        <div class="float-right">
          <HMSwitch
            v-model="teacher.send_payment_plan_emails"
            @change="setPaymentPlanCancellationEmailsEnabled"
          />
        </div>
      </div>
      <div class="col-6 align-self-center mb-2">
        <h5 class="mb-0">
          {{ $t('SETTINGS.LABEL.SEND_EMAIL_ON_SUBSCRIPTION_TERMINATION') }}
        </h5>
      </div>
      <div class="col-6 align-self-center mb-2">
        <div class="float-right">
          <HMSwitch
            v-model="teacher.send_subscription_termination_email"
            @change="setSubscriptionTerminationEmailsEnabled"
          />
        </div>
      </div>
      <div class="col-6 align-self-center mb-2">
        <h5 class="mb-0">
          {{ $t('SETTINGS.LABEL.SEND_EMAIL_ON_EVENT_REGISTER_UNREGISTER') }}
        </h5>
      </div>
      <div class="col-6 align-self-center mb-2">
        <div class="float-right">
          <HMSwitch
            v-model="teacher.send_event_registration_email"
            @change="setEventRegisterUnregisterEmailsEnabled"
          />
        </div>
      </div>
      <div class="col-6 align-self-center mb-2">
        <h5 class="mb-0">
          {{ $t('SETTINGS.LABEL.SEND_EMAIL_ON_EVENT_RECORDING_REGISTRATION') }}
        </h5>
      </div>
      <div class="col-6 align-self-center mb-2">
        <div class="float-right">
          <HMSwitch
            v-model="teacher.send_recording_registration_email"
            @change="setEventRecordingRegisterEmailsEnabled"
          />
        </div>
      </div>
    </div>
    <hr class="mt-1 mb-4" />
    <div class="row mb-4">
      <div class="col-12 h2 mb-4">
        {{ $t('SETTINGS.TITLE.CHECKOUT_PREFERENCES') }}
      </div>
      <div class="col-12 d-flex flex-column">
        <div class="d-flex flex-row justify-content-between align-items-center">
          <h5 class="mb-0">
            {{ $t('SETTINGS.LABEL.CHECKOUT_COLLECT_PHONE') }}
          </h5>
          <HMSwitch
            v-model="checkoutSettings.phone_number_displayed"
            @change="updateTeacherCheckoutSettings"
          />
        </div>
        <HMCheckBox
          class="mt-2 small"
          v-if="checkoutSettings.phone_number_displayed"
          v-model="checkoutSettings.phone_number_required"
          @input="updateTeacherCheckoutSettings"
        >
          {{ $t('SETTINGS.LABEL.REQUIRE_FOR_CHECKOUT') }}
        </HMCheckBox>
      </div>
      <div class="col-12 d-flex flex-column mt-3">
        <div class="d-flex flex-row justify-content-between align-items-center">
          <h5 class="mb-0">
            {{ $t('SETTINGS.LABEL.CHECKOUT_COLLECT_ADDRESS') }}
          </h5>
          <HMSwitch
            v-model="checkoutSettings.address_displayed"
            @change="updateTeacherCheckoutSettings"
          />
        </div>
        <HMCheckBox
          class="mt-2 small"
          v-if="checkoutSettings.address_displayed"
          v-model="checkoutSettings.address_required"
          @input="updateTeacherCheckoutSettings"
        >
          {{ $t('SETTINGS.LABEL.REQUIRE_FOR_CHECKOUT') }}
        </HMCheckBox>
      </div>
      <div class="col-12 d-flex flex-column mt-3">
        <div class="d-flex flex-row justify-content-between align-items-center">
          <h5 class="mb-0">
            {{ $t('SETTINGS.LABEL.CHECKOUT_COLLECT_CITY') }}
          </h5>
          <HMSwitch
            v-model="checkoutSettings.city_displayed"
            @change="updateTeacherCheckoutSettings"
          />
        </div>
        <HMCheckBox
          class="mt-2 small"
          v-if="checkoutSettings.city_displayed"
          v-model="checkoutSettings.city_required"
          @input="updateTeacherCheckoutSettings"
        >
          {{ $t('SETTINGS.LABEL.REQUIRE_FOR_CHECKOUT') }}
        </HMCheckBox>
      </div>
      <div class="col-12 d-flex flex-column mt-3">
        <div class="d-flex flex-row justify-content-between align-items-center">
          <h5 class="mb-0">
            {{ $t('SETTINGS.LABEL.CHECKOUT_COLLECT_ZIPCODE') }}
          </h5>
          <HMSwitch
            v-model="checkoutSettings.zip_displayed"
            @change="updateTeacherCheckoutSettings"
          />
        </div>
        <HMCheckBox
          class="mt-2 small"
          v-if="checkoutSettings.zip_displayed"
          v-model="checkoutSettings.zip_required"
          @input="updateTeacherCheckoutSettings"
        >
          {{ $t('SETTINGS.LABEL.REQUIRE_FOR_CHECKOUT') }}
        </HMCheckBox>
      </div>
      <div class="col-12 d-flex flex-column mt-3">
        <div class="d-flex flex-row justify-content-between align-items-center">
          <h5 class="mb-0">
            {{ $t('SETTINGS.LABEL.CHECKOUT_COLLECT_STATE') }}
          </h5>
          <HMSwitch
            v-model="checkoutSettings.state_displayed"
            @change="updateTeacherCheckoutSettings"
          />
        </div>
        <HMCheckBox
          class="mt-2 small"
          v-if="checkoutSettings.state_displayed"
          v-model="checkoutSettings.state_required"
          @input="updateTeacherCheckoutSettings"
        >
          {{ $t('SETTINGS.LABEL.REQUIRE_FOR_CHECKOUT') }}
        </HMCheckBox>
      </div>
      <div class="col-12 d-flex flex-column mt-3">
        <div class="d-flex flex-row justify-content-between align-items-center">
          <h5 class="mb-0">
            {{ $t('SETTINGS.LABEL.CHECKOUT_COLLECT_COUNTRY') }}
          </h5>
          <HMSwitch
            v-model="checkoutSettings.country_displayed"
            @change="updateTeacherCheckoutSettings"
          />
        </div>
        <HMCheckBox
          class="mt-2 small"
          v-if="checkoutSettings.country_displayed"
          v-model="checkoutSettings.country_required"
          @input="updateTeacherCheckoutSettings"
        >
          {{ $t('SETTINGS.LABEL.REQUIRE_FOR_CHECKOUT') }}
        </HMCheckBox>
      </div>
      <div class="col-12 d-flex flex-column mt-3">
        <div class="d-flex flex-row justify-content-between align-items-center">
          <h5 class="mb-0">
            {{ $t('SETTINGS.LABEL.CHECKOUT_COLLECT_BUSINESS_NAME') }}
          </h5>
          <HMSwitch
            v-model="checkoutSettings.business_name_displayed"
            @change="updateTeacherCheckoutSettings"
          />
        </div>
        <HMCheckBox
          class="mt-2 small"
          v-if="checkoutSettings.business_name_displayed"
          v-model="checkoutSettings.business_name_required"
          @input="updateTeacherCheckoutSettings"
        >
          {{ $t('SETTINGS.LABEL.REQUIRE_FOR_CHECKOUT') }}
        </HMCheckBox>
      </div>
      <div class="col-12 d-flex flex-column mt-3">
        <div class="d-flex flex-row justify-content-between align-items-center">
          <h5 class="mb-0">
            {{ $t('SETTINGS.LABEL.CHECKOUT_COLLECT_TAX_ID') }}
          </h5>
          <HMSwitch
            v-model="checkoutSettings.tax_id_displayed"
            @change="updateTeacherCheckoutSettings"
          />
        </div>
        <HMCheckBox
          class="mt-2 small"
          v-if="checkoutSettings.tax_id_displayed"
          v-model="checkoutSettings.tax_id_required"
          @input="updateTeacherCheckoutSettings"
        >
          {{ $t('SETTINGS.LABEL.REQUIRE_FOR_CHECKOUT') }}
        </HMCheckBox>
      </div>
    </div>
    <hr class="mt-1 mb-4" />
    <div class="row">
      <div class="col">
        <h2>{{ $t('SETTINGS.TITLE.PRODUCT_RATINGS_AND_REVIEWS') }}</h2>
      </div>
      <div class="col align-self-center">
        <div class="float-right d-flex">
          <HMSwitch
            :value="teacher.product_reviews_enabled"
            @change="updateProductdReviewsEnabled"
            :disabled="productdReviewsEnabledSwichLoading"
          />
        </div>
      </div>
    </div>
    <div class="row mt-3 mb-4">
      <div class="col-12">
        <p>{{ $t('SETTINGS.TEXT.WHEN_TURNED_ON_CUSTOMERS_CAN') }}</p>
        <label class="m-0">
          {{ $t('SETTINGS.LABEL.RATINGS_ICON') }}
        </label>
        <v-select
          class="mt-3"
          :options="ratingIconTypes"
          code="type"
          :value="ratingIconType"
          @input="updateRatingIconType"
          :disabled="updateRatingIconTypeInProgress"
        >
          <template v-slot:selected-option="option">
            <span class="d-inline-block text-truncate">{{ option.label }}</span>
            <span :class="option.icon" class="ml-2 c-coral"></span>
          </template>
          <template v-slot:option="option">
            {{ option.label }}
            <span :class="option.icon" class="ml-1"></span>
          </template>
        </v-select>
      </div>
    </div>
    <hr class="mt-1 mb-4" />
    <div class="row" v-if="isActiveSubscription">
      <div class="col">
        <h2>{{ $t('SETTINGS.TITLE.DELETE_ACCOUNT') }}</h2>
      </div>
      <div class="col align-self-center">
        <div class="float-right d-flex">
          <NavItem
            icon="far fa-trash"
            :text="$t('SETTINGS.BUTTON.DELETE_ACCOUNT')"
            :iconOnRight="true"
            @click.native="cancelAccount"
          />
        </div>
      </div>
    </div>
    <div class="row mt-3 mb-4">
      <div class="col-12" v-if="isActiveSubscription">
        {{ $t('SETTINGS.TEXT.CANCEL_ACCOUNT') }}
      </div>
      <div class="col-12" v-else>
        {{
          $t('SETTINGS.TEXT.CANCELED_ACCOUNT', {
            expires: formatDate(
              accountInfo.plan_information.expires,
              'MMM D, YYYY'
            ),
          })
        }}
      </div>
      <div class="col-12 font-weight-bold mt-3 c-charcoal">
        {{ $t('SETTINGS.TEXT.CANCEL_ACCOUNT_SUBSCRIPTIONS_WARNING') }}
      </div>
    </div>
    <hr class="mt-1 mb-4" />
  </div>
</template>

<script>
import Vue from 'vue';

import TopNav from '@/web/components/navigation/TopNav.vue';
import HMInfoTile from '@/web/components/common/HMInfoTile';
import HMTextInput from '@/web/components/common/HMTextInput';
import { mapState, mapGetters } from 'vuex';
import moment from 'moment';
import ChangePaymentCardModal from '@/web/components/settings/ChangePaymentCardModal.vue';
import { pick } from 'lodash';
import NavItem from '@/web/components/navigation/NavItem.vue';
import timezones from '@/web/utils/timezones.ts';
import EditAccountContactInfoModal from '@/web/components/settings/EditAccountContactInfoModal';
import { errorToMessage } from '@/core/utils/errors';
import HMCheckBox from '../../../components/common/HMCheckBox';
import CancelTeacherAccountModal from '../../../components/account/CancelTeacherAccountModal';

export default Vue.extend({
  components: {
    HMCheckBox,
    HMTextInput,
    TopNav,
    HMInfoTile,
    NavItem,
  },
  name: 'Account',
  data() {
    return {
      passwordFormOpen: false,
      inProgress: false,
      contactFormModel: null,
      timezones: timezones,
      newPassword1: null,
      newPassword2: null,
      formError: null,
      checkoutSettings: {},
      ratingIconTypes: [
        {
          icon: 'far fa-star',
          label: this.$t('SETTINGS.LABEL.STAR'),
          type: 'stars',
        },
        {
          icon: 'far fa-heart',
          label: this.$t('SETTINGS.LABEL.HEART'),
          type: 'hearts',
        },
        {
          icon: 'far fa-circle',
          label: this.$t('SETTINGS.LABEL.CIRCLE'),
          type: 'circles',
        },
      ],
      productdReviewsEnabledSwichLoading: false,
      updateRatingIconTypeInProgress: false,
    };
  },
  computed: {
    ...mapGetters('user', ['teacher', 'user']),
    ...mapState('setting', ['accountInfo']),
    teacherInfoShorthand() {
      const {
        first_name,
        last_name,
        studio_name,
        web_link,
        email,
      } = this.teacher;
      return [studio_name, `${first_name} ${last_name}`, email, web_link]
        .filter(item => item)
        .join(', ');
    },
    calculatedStorageInfo() {
      if (!this.accountInfo?.plan_information?.storage_available) {
        return;
      }
      const storageUsed = (
        this.accountInfo?.plan_information?.storage_used || 0
      ).toFixed();
      const storageAvailable = this.accountInfo.plan_information
        .storage_available;
      return `${storageUsed}/${storageAvailable}`;
    },
    isActiveSubscription() {
      const subscriptionIsActive = ['active', 'trialing'].includes(
        this.accountInfo?.plan_information?.subscription_status
      );
      const subscriptionCancelsAtPeriodEnd = this.accountInfo?.plan_information
        ?.cancel_at_period_end;
      return subscriptionIsActive && !subscriptionCancelsAtPeriodEnd;
    },
    ratingIconType() {
      return this.ratingIconTypes.find(
        icon => icon.type === this.teacher?.rating_icon_type
      );
    },
  },
  methods: {
    async updateProductdReviewsEnabled(status) {
      this.productdReviewsEnabledSwichLoading = true;
      try {
        await this.$store.dispatch('user/updateTeacher', {
          teacher: {
            product_reviews_enabled: status,
          },
        });
        this.productdReviewsEnabledSwichLoading = false;
      } catch (e) {
        this.$notify({
          type: 'error',
          title: this.$t('SETTINGS.TEXT.ERROR'),
          text: errorToMessage(e),
        });
      }
    },
    getCardLogo(type) {
      const cardType = type?.toLowerCase();
      if (cardType === 'visa') {
        return '/assets/images/cards/visa_logo.png';
      }
      if (cardType === 'mastercard') {
        return '/assets/images/cards/mastercard_logo.png';
      }
      if (cardType === 'american express') {
        return '/assets/images/cards/amex_logo.png';
      } else {
        return '/assets/images/cards/other_card_logo.png';
      }
    },
    formatDate(dateString, format, emptyLabelOverride) {
      if (!dateString) {
        return emptyLabelOverride || '';
      }
      return moment(dateString).format(format);
    },
    editCardInfo() {
      this.$modal.show(ChangePaymentCardModal);
    },
    openTeacherInfoForm() {
      this.$modal.show(EditAccountContactInfoModal, {
        contact: this.teacherToFormModel(this.teacher),
      });
    },
    async updateTeacherCheckoutSettings() {
      await this.$store.dispatch(
        'setting/updateCheckoutSettings',
        this.checkoutSettings
      );
    },
    teacherToFormModel(teacher) {
      return pick(teacher, [
        'first_name',
        'last_name',
        'studio_name',
        'web_link',
        'address',
        'city',
        'state',
        'country',
        'zipcode',
        'phone',
        'email',
        'additional_invoice_info',
      ]);
    },
    openPasswordForm() {
      this.passwordFormOpen = true;
    },
    closePasswordForm() {
      this.passwordFormOpen = false;
    },
    async updatePassword() {
      this.inProgress = true;
      try {
        this.formError = null;
        const data = {
          newPassword1: this.newPassword1,
          newPassword2: this.newPassword2,
        };
        await this.$store.dispatch('user/changePassword', data);
        this.inProgress = false;
        this.passwordFormOpen = false;
      } catch (error) {
        this.formError = error?.response?.data?.new_password2?.[0];
        this.inProgress = false;
      }
    },
    async setTimezone() {
      try {
        await this.$store.dispatch('user/updateTeacher', {
          timezone: this.user.timezone,
        });
      } catch (error) {
        this.formError = error;
      }
    },
    async setNewPurchaseEmailsEnabled(enabled) {
      const data = { teacher: { send_new_sale_email: enabled } };
      try {
        await this.$store.dispatch('user/updateTeacher', data);
      } catch (error) {
        this.$notify({
          group: 'app',
          type: 'error',
          title: 'Failed to set email preference',
          text: errorToMessage(error.response, `Request failed: ${error}`),
          duration: -1, // stay ON
        });
      }
    },
    async setNewTipEmailsEnabled(enabled) {
      const data = { teacher: { send_new_tip_email: enabled } };
      try {
        await this.$store.dispatch('user/updateTeacher', data);
      } catch (error) {
        this.$notify({
          group: 'app',
          type: 'error',
          title: 'Failed to set email preference',
          text: errorToMessage(error.response, `Request failed: ${error}`),
          duration: -1, // stay ON
        });
      }
    },
    async setSubscriptionCancellationEmailsEnabled(enabled) {
      const data = {
        teacher: { send_subscription_cancellation_email: enabled },
      };
      try {
        await this.$store.dispatch('user/updateTeacher', data);
      } catch (error) {
        this.$notify({
          group: 'app',
          type: 'error',
          title: 'Failed to set email preference',
          text: errorToMessage(error.response, `Request failed: ${error}`),
          duration: -1, // stay ON
        });
      }
    },
    async setPaymentPlanCancellationEmailsEnabled(enabled) {
      const data = {
        teacher: { send_payment_plan_emails: enabled },
      };
      try {
        await this.$store.dispatch('user/updateTeacher', data);
      } catch (error) {
        this.$notify({
          group: 'app',
          type: 'error',
          title: 'Failed to set email preference',
          text: errorToMessage(error.response, `Request failed: ${error}`),
          duration: -1, // stay ON
        });
      }
    },
    async setSubscriptionTerminationEmailsEnabled(enabled) {
      const data = {
        teacher: { send_subscription_termination_email: enabled },
      };
      try {
        await this.$store.dispatch('user/updateTeacher', data);
      } catch (error) {
        this.$notify({
          group: 'app',
          type: 'error',
          title: 'Failed to set email preference',
          text: errorToMessage(error.response, `Request failed: ${error}`),
          duration: -1, // stay ON
        });
      }
    },
    async setEventRegisterUnregisterEmailsEnabled(enabled) {
      const data = { teacher: { send_event_registration_email: enabled } };
      try {
        await this.$store.dispatch('user/updateTeacher', data);
      } catch (error) {
        this.$notify({
          group: 'app',
          type: 'error',
          title: 'Failed to set email preference',
          text: errorToMessage(error.response, `Request failed: ${error}`),
          duration: -1, // stay ON
        });
      }
    },
    async setEventRecordingRegisterEmailsEnabled(enabled) {
      const data = { teacher: { send_recording_registration_email: enabled } };
      try {
        await this.$store.dispatch('user/updateTeacher', data);
      } catch (error) {
        this.$notify({
          group: 'app',
          type: 'error',
          title: 'Failed to set email preference',
          text: errorToMessage(error.response, `Request failed: ${error}`),
          duration: -1, // stay ON
        });
      }
    },
    async updateRatingIconType({ type }) {
      const data = { teacher: { rating_icon_type: type } };
      this.updateRatingIconTypeInProgress = true;
      try {
        await this.$store.dispatch('user/updateTeacher', data);
      } catch (error) {
        this.$notify({
          group: 'app',
          type: 'error',
          title: 'Failed to set rating icon',
          text: errorToMessage(error.response, `Request failed: ${error}`),
          duration: -1, // stay ON
        });
      }
      this.updateRatingIconTypeInProgress = false;
    },
    cancelAccount() {
      this.$modal.show(CancelTeacherAccountModal, {
        accountCanceled: async payload => {
          try {
            await this.$store.dispatch('setting/cancelSubscription', payload);
            await this.$store.dispatch('setting/getAccountInfo');
            this.$notify({
              group: 'app',
              type: 'success',
              title: this.$t('NOTIFICATIONS.TITLE.SUCCESS'),
              text: this.$t(
                'SETTINGS.NOTIFICATIONS.SUBSCRIPTION_SUCCESSFULLY_CANCELLED'
              ),
            });
          } catch (error) {
            this.$notify({
              group: 'app',
              type: 'error',
              title:
                'Failed to cancel your subscription. Please contact support.',
              text: errorToMessage(error?.response, `Request failed: ${error}`),
              duration: -1, // stay ON
            });
          }
        },
      });
    },
  },
  async mounted() {
    await this.$store.dispatch('setting/getAccountInfo');
    this.checkoutSettings = await this.$store.dispatch(
      'setting/getCheckoutSettings'
    );
  },
});
</script>

<style lang="scss" scoped>
@import '@/web/styles/components-variables.scss';

.gray-background {
  background-color: $light-gray;
}
.password-form {
  background-color: $light-gray;
}
</style>
